import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { forkJoin, of } from 'rxjs';
import { Company } from '../../company/company.model';
import { CompanyService } from '../../company/company.service';
import { ContractsService } from '../contracts.service';
import { ContractCrmContext } from '../models/contract-crm-context.model';
import { Contracts } from '../models/contracts.model';
import {
    buildContractPartyOptions,
    ContractPartyOption,
    PartyMode,
    resolvePartySelection,
    resolvePreferredRepresentative
} from '../party-resolution/contract-party-resolution';
import { CrmService } from '../../crm/crm.service';
import { Customer } from '../../customer/customer.model';
import { CustomerFormComponent } from '../../customer/customer-form/customer-form.component';
import { CustomerService } from '../../customer/customer.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

@Component({
    selector: 'app-add-edit-contract',
    templateUrl: './add-edit-contract.component.html',
    styleUrls: ['./add-edit-contract.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        InputTextModule,
        ButtonModule,
        SelectModule,
        BreadcrumbModule,
        DatePickerModule,
        FileUploadModule,
        DialogModule,
        CustomerFormComponent,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        TagModule
    ],
    providers: [MessageService]
})
export class AddEditContractComponent implements OnInit {

    createEditContractForm!: FormGroup;
    createEditContract: Contracts = new Contracts();
    contractCreationInProgress = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    firstPartyReps: Customer[] = [];
    secondPartyReps: Customer[] = [];
    firstPartyMode: PartyMode | null = null;
    secondPartyMode: PartyMode | null = null;
    firstPartyCrmOptions: ContractPartyOption[] = [];
    secondPartyCrmOptions: ContractPartyOption[] = [];
    selectedFirstPartyCrmOption: ContractPartyOption | null = null;
    selectedSecondPartyCrmOption: ContractPartyOption | null = null;
    firstPartyModeOptions = [
        { label: 'My company', value: 'SELF' as PartyMode },
        { label: 'CRM supplier', value: 'CRM' as PartyMode }
    ];
    secondPartyModeOptions = [
        { label: 'My company', value: 'SELF' as PartyMode },
        { label: 'CRM client', value: 'CRM' as PartyMode }
    ];
    showCustomerForm = false;
    customerFormHeader: string;
    customerFormSubmitLabel: string;
    selectedCompany: Company = new Company();
    selectedCustomer: Customer = new Customer();
    currentParty: 'firstParty' | 'secondParty';
    crmContext?: ContractCrmContext;
    isCrmContextLoading = false;

    private contractId?: number;
    private partyOptionsLoaded = false;
    private editModeInitialized = false;
    private createModeInitialized = false;
    private existingContractLoaded = false;
    private sessionCompany?: Company;
    private sessionCustomer?: Customer;

    constructor(
        private fb: FormBuilder,
        private contractsService: ContractsService,
        private crmService: CrmService,
        private customerService: CustomerService,
        private companyService: CompanyService,
        private sessionStorageService: SessionStorageService,
        private route: ActivatedRoute,
        private location: Location,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.sessionCompany = this.sessionStorageService.getCompany();
        this.sessionCustomer = this.sessionStorageService.getCustomer();
        this.contractId = this.readContractId();

        this.createEditContractForm = this.fb.group({
            contractNumberInput: [''],
            firstPartyDropdown: [null, Validators.required],
            firstPartyRepDropdown: [null, Validators.required],
            secondPartyDropdown: [null, Validators.required],
            secondPartyRepDropdown: [null, Validators.required],
            statusInput: [''],
            startDateInput: [''],
            endDateInput: ['']
        });

        if (!this.contractId) {
            this.applyCrmQueryContext();
        } else {
            this.loadContract(this.contractId);
        }

        this.loadPartyOptions();

        this.breadcrumbItems = [
            { label: 'Contracts', routerLink: '/contracts' },
            { label: this.contractId ? 'Edit Contract' : 'Add Contract' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
    }

    loadContract(contractId: number) {
        this.contractsService.getContractById(contractId).subscribe(contract => {
            this.createEditContract = contract;
            this.existingContractLoaded = true;
            this.updateFormValues();
            this.tryInitializePartySelections();
            this.loadCrmContextForContract(contract.contractID);
        });
    }

    updateFormValues() {
        this.createEditContractForm.patchValue({
            contractNumberInput: this.createEditContract.contractNumber,
            statusInput: this.getCurrentStatus(),
            startDateInput: this.createEditContract.createdDate,
            endDateInput: this.createEditContract.updatedDate
        });
    }

    createUpdateContract() {
        if (this.createEditContractForm.invalid) {
            this.createEditContractForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Missing required fields',
                detail: 'Select both seller and buyer parties with their representatives before creating the agreement.'
            });
            return;
        }
        this.syncContractFromForm();
        this.contractCreationInProgress = true;
        if (this.createEditContract.contractID) {
            this.contractsService.updateContract(this.createEditContract.contractID, this.createEditContract)
                .subscribe(() => {
                    this.contractCreationInProgress = false;
                    this.location.back();
                }, error => {
                    console.error(error);
                    this.contractCreationInProgress = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: this.getErrorDetail(error, 'Error updating contract.') });
                });
        } else {
            this.contractsService.createContract(this.createEditContract)
                .subscribe(() => {
                    this.contractCreationInProgress = false;
                    this.location.back();
                }, error => {
                    console.error(error);
                    this.contractCreationInProgress = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: this.getErrorDetail(error, 'Error creating contract.') });
                });
        }
    }

    clearContractForm() {
        const crmClientId = this.createEditContract.crmClientId;
        const crmAccountId = this.createEditContract.crmAccountId;
        const sourceLeadId = this.createEditContract.sourceLeadId;
        const sourceProspectId = this.createEditContract.sourceProspectId;
        const sourceQuotationId = this.createEditContract.sourceQuotationId;
        const kamCustomerId = this.createEditContract.kamCustomerId;

        this.createEditContractForm.reset();
        this.firstPartyReps = [];
        this.secondPartyReps = [];
        this.firstPartyMode = null;
        this.secondPartyMode = null;
        this.selectedFirstPartyCrmOption = null;
        this.selectedSecondPartyCrmOption = null;
        this.createEditContract = new Contracts();
        this.createEditContract.crmClientId = crmClientId;
        this.createEditContract.crmAccountId = crmAccountId;
        this.createEditContract.sourceLeadId = sourceLeadId;
        this.createEditContract.sourceProspectId = sourceProspectId;
        this.createEditContract.sourceQuotationId = sourceQuotationId;
        this.createEditContract.kamCustomerId = kamCustomerId;
        this.createModeInitialized = false;
        this.editModeInitialized = false;
        this.existingContractLoaded = !this.contractId;

        if (crmClientId) {
            this.loadCrmContextForClient(crmClientId);
        } else {
            this.crmContext = undefined;
        }

        this.tryInitializePartySelections();
    }

    getCurrentStatus(): string {
        return [...(this.createEditContract.revisions ?? [])]
            .sort((a, b) => a.revisionNumber - b.revisionNumber)
            .map(value => value.status)
            .pop() || 'Draft';
    }

    onContractFileUpload(event: any) {
        console.log(event.files);
    }

    onFirstPartyModeChanged(mode: PartyMode | null) {
        this.firstPartyMode = mode;
        if (mode === 'SELF') {
            this.selectedFirstPartyCrmOption = null;
            this.applySelfPartySelection('firstParty');
            return;
        }
        this.clearResolvedParty('firstParty');
    }

    onSecondPartyModeChanged(mode: PartyMode | null) {
        this.secondPartyMode = mode;
        if (mode === 'SELF') {
            this.selectedSecondPartyCrmOption = null;
            this.applySelfPartySelection('secondParty');
            return;
        }
        this.clearResolvedParty('secondParty');
    }

    onFirstPartyCrmOptionChanged(option?: ContractPartyOption | null) {
        this.selectedFirstPartyCrmOption = option || null;
        if (!option) {
            this.clearResolvedParty('firstParty');
            return;
        }
        this.applyCrmPartySelection('firstParty', option);
    }

    onSecondPartyCrmOptionChanged(option?: ContractPartyOption | null) {
        this.selectedSecondPartyCrmOption = option || null;
        if (!option) {
            this.clearResolvedParty('secondParty');
            return;
        }
        this.applyCrmPartySelection('secondParty', option);
    }

    registerPartyRep(party: 'firstParty' | 'secondParty') {
        const company = this.getResolvedCompany(party);
        if (!company) {
            return;
        }
        this.currentParty = party;
        this.customerFormHeader = 'Create New Representative';
        this.customerFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer();
        this.selectedCompany = company;
        this.showCustomerForm = true;
    }

    onCustomerFormClosed() {
        this.showCustomerForm = false;
        const company = this.getResolvedCompany(this.currentParty);
        if (!company) {
            return;
        }
        this.loadRepresentativesForParty(this.currentParty, company, this.selectedCustomer);
    }

    getHealthFlags(): string[] {
        return this.crmContext?.healthFlags ?? [];
    }

    isCreateDisabled(): boolean {
        return this.contractCreationInProgress || this.createEditContractForm.invalid;
    }

    getResolvedCompany(party: 'firstParty' | 'secondParty'): Company | undefined {
        return party === 'firstParty'
            ? this.createEditContractForm.get('firstPartyDropdown')?.value || this.createEditContract.firstParty
            : this.createEditContractForm.get('secondPartyDropdown')?.value || this.createEditContract.secondParty;
    }

    canRegisterPartyRep(party: 'firstParty' | 'secondParty'): boolean {
        return !!this.getResolvedCompany(party)?.id;
    }

    private loadPartyOptions() {
        const countryId = this.sessionStorageService.getCountryList()?.find(country => country.countryCode === 'SA')?.id;
        const tenantCompanyId = this.sessionCompany?.id;
        const companies$ = countryId ? this.companyService.getCompaniesByCountry(countryId) : of([] as Company[]);
        const clients$ = tenantCompanyId ? this.crmService.getClients(tenantCompanyId) : of([]);

        forkJoin({ companies: companies$, clients: clients$ }).subscribe({
            next: ({ companies, clients }) => {
                const options = buildContractPartyOptions(clients, companies || []);
                this.firstPartyCrmOptions = options.sellerOptions;
                this.secondPartyCrmOptions = options.buyerOptions;
                this.partyOptionsLoaded = true;
                this.tryInitializePartySelections();
            },
            error: () => {
                this.partyOptionsLoaded = true;
                this.tryInitializePartySelections();
            }
        });
    }

    private tryInitializePartySelections() {
        if (!this.partyOptionsLoaded) {
            return;
        }

        if (this.contractId) {
            if (!this.existingContractLoaded || this.editModeInitialized) {
                return;
            }
            this.hydrateExistingContractSelections();
            this.editModeInitialized = true;
            return;
        }

        if (this.createModeInitialized) {
            return;
        }

        this.initializeCreateSelections();
        this.createModeInitialized = true;
    }

    private initializeCreateSelections() {
        this.firstPartyMode = 'SELF';
        this.selectedFirstPartyCrmOption = null;
        this.applySelfPartySelection('firstParty', { clearCrmContext: false });

        if (this.createEditContract.crmClientId) {
            const option = this.secondPartyCrmOptions.find(value => value.crmClientId === this.createEditContract.crmClientId) || null;
            if (option) {
                this.secondPartyMode = 'CRM';
                this.selectedSecondPartyCrmOption = option;
                this.applyCrmPartySelection('secondParty', option, { crmClientId: option.crmClientId, loadCrmContext: false });
                return;
            }
            this.messageService.add({
                severity: 'warn',
                summary: 'CRM buyer unavailable',
                detail: 'The linked CRM client has no company mapping and cannot be used as the buyer.'
            });
        }

        this.secondPartyMode = null;
        this.selectedSecondPartyCrmOption = null;
        this.clearResolvedParty('secondParty', false);
    }

    private hydrateExistingContractSelections() {
        const firstPartySelection = resolvePartySelection(this.createEditContract.firstParty, this.sessionCompany, this.firstPartyCrmOptions);
        if (firstPartySelection?.mode === 'SELF') {
            this.firstPartyMode = 'SELF';
            this.selectedFirstPartyCrmOption = null;
            this.applySelfPartySelection('firstParty', {
                selectedRep: this.createEditContract.firstPartyRep,
                clearCrmContext: false
            });
        } else if (firstPartySelection?.option) {
            this.firstPartyMode = 'CRM';
            this.selectedFirstPartyCrmOption = this.ensureCrmOption('firstParty', firstPartySelection.option);
            this.applyCrmPartySelection('firstParty', this.selectedFirstPartyCrmOption, {
                selectedRep: this.createEditContract.firstPartyRep,
                loadCrmContext: false
            });
        }

        const secondPartySelection = resolvePartySelection(
            this.createEditContract.secondParty,
            this.sessionCompany,
            this.secondPartyCrmOptions,
            this.createEditContract.crmClientId
        );

        if (secondPartySelection?.mode === 'SELF') {
            this.secondPartyMode = 'SELF';
            this.selectedSecondPartyCrmOption = null;
            this.applySelfPartySelection('secondParty', {
                selectedRep: this.createEditContract.secondPartyRep,
                clearCrmContext: false
            });
        } else if (secondPartySelection?.option) {
            this.secondPartyMode = 'CRM';
            this.selectedSecondPartyCrmOption = this.ensureCrmOption('secondParty', secondPartySelection.option);
            this.applyCrmPartySelection('secondParty', this.selectedSecondPartyCrmOption, {
                selectedRep: this.createEditContract.secondPartyRep,
                crmClientId: this.createEditContract.crmClientId ?? this.selectedSecondPartyCrmOption.crmClientId,
                loadCrmContext: false
            });
        }
    }

    private applySelfPartySelection(
        party: 'firstParty' | 'secondParty',
        options?: { selectedRep?: Customer; clearCrmContext?: boolean }
    ) {
        if (!this.sessionCompany) {
            this.clearResolvedParty(party, options?.clearCrmContext !== false);
            return;
        }
        if (party === 'secondParty' && options?.clearCrmContext !== false) {
            this.clearBuyerCrmContext();
        }
        this.applyResolvedPartyCompany(party, this.sessionCompany, options?.selectedRep || this.sessionCustomer || null);
    }

    private applyCrmPartySelection(
        party: 'firstParty' | 'secondParty',
        option: ContractPartyOption,
        options?: { selectedRep?: Customer; crmClientId?: number | null; loadCrmContext?: boolean }
    ) {
        if (party === 'secondParty') {
            this.createEditContract.crmClientId = options?.crmClientId ?? option.crmClientId ?? undefined;
            this.createEditContract.crmAccountId = undefined;
            if (options?.loadCrmContext !== false && this.createEditContract.crmClientId) {
                this.loadCrmContextForClient(this.createEditContract.crmClientId);
            }
        }
        this.applyResolvedPartyCompany(party, option.company, options?.selectedRep || null, option);
    }

    private applyResolvedPartyCompany(
        party: 'firstParty' | 'secondParty',
        company: Company,
        selectedRep?: Customer | null,
        option?: ContractPartyOption | null
    ) {
        this.patchResolvedCompany(party, company);
        this.loadRepresentativesForParty(party, company, selectedRep || null, option);
    }

    private loadRepresentativesForParty(
        party: 'firstParty' | 'secondParty',
        company?: Company,
        selectedRep?: Customer | null,
        option?: ContractPartyOption | null
    ) {
        if (!company?.id) {
            this.patchResolvedRepresentative(party, null);
            if (party === 'firstParty') {
                this.firstPartyReps = [];
            } else {
                this.secondPartyReps = [];
            }
            return;
        }

        this.customerService.getCustomersByCompany(company.id).subscribe(customers => {
            if (party === 'firstParty') {
                this.firstPartyReps = customers;
            } else {
                this.secondPartyReps = customers;
            }

            const matchedRep = selectedRep?.id
                ? customers.find(customer => customer.id === selectedRep.id) || null
                : null;
            const preferredRep = matchedRep || resolvePreferredRepresentative(customers, option);
            this.patchResolvedRepresentative(party, preferredRep || null);
        });
    }

    private patchResolvedCompany(party: 'firstParty' | 'secondParty', company: Company | null) {
        if (party === 'firstParty') {
            this.createEditContract.firstParty = company as Company;
            this.createEditContractForm.patchValue({ firstPartyDropdown: company });
            return;
        }
        this.createEditContract.secondParty = company as Company;
        this.createEditContractForm.patchValue({ secondPartyDropdown: company });
    }

    private patchResolvedRepresentative(party: 'firstParty' | 'secondParty', customer: Customer | null) {
        if (party === 'firstParty') {
            this.createEditContract.firstPartyRep = customer as Customer;
            this.createEditContractForm.patchValue({ firstPartyRepDropdown: customer });
            return;
        }
        this.createEditContract.secondPartyRep = customer as Customer;
        this.createEditContractForm.patchValue({ secondPartyRepDropdown: customer });
    }

    private clearResolvedParty(party: 'firstParty' | 'secondParty', clearCrmContext: boolean = true) {
        this.patchResolvedCompany(party, null);
        this.patchResolvedRepresentative(party, null);
        if (party === 'firstParty') {
            this.firstPartyReps = [];
            return;
        }
        this.secondPartyReps = [];
        if (clearCrmContext) {
            this.clearBuyerCrmContext();
        }
    }

    private clearBuyerCrmContext() {
        this.createEditContract.crmClientId = undefined;
        this.createEditContract.crmAccountId = undefined;
        this.crmContext = undefined;
        this.isCrmContextLoading = false;
    }

    private ensureCrmOption(party: 'firstParty' | 'secondParty', option: ContractPartyOption): ContractPartyOption {
        const collection = party === 'firstParty' ? this.firstPartyCrmOptions : this.secondPartyCrmOptions;
        const existing = collection.find(value => value.key === option.key);
        if (existing) {
            return existing;
        }
        const updated = [option, ...collection];
        if (party === 'firstParty') {
            this.firstPartyCrmOptions = updated;
        } else {
            this.secondPartyCrmOptions = updated;
        }
        return option;
    }

    private applyCrmQueryContext() {
        this.createEditContract.crmClientId = this.readNumberQueryParam('crmClientId');
        this.createEditContract.crmAccountId = this.readNumberQueryParam('crmAccountId');
        this.createEditContract.sourceLeadId = this.readNumberQueryParam('sourceLeadId');
        this.createEditContract.sourceProspectId = this.readNumberQueryParam('sourceProspectId');
        this.createEditContract.sourceQuotationId = this.readNumberQueryParam('sourceQuotationId');
        this.createEditContract.kamCustomerId = this.readNumberQueryParam('kamCustomerId');
        if (this.createEditContract.crmClientId) {
            this.loadCrmContextForClient(this.createEditContract.crmClientId);
        }
    }

    private readContractId(): number | undefined {
        const contractId = Number(this.route.snapshot.paramMap.get('id'));
        return Number.isFinite(contractId) && contractId > 0 ? contractId : undefined;
    }

    private readNumberQueryParam(name: string): number | undefined {
        const value = this.route.snapshot.queryParamMap.get(name);
        if (!value) {
            return undefined;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    private loadCrmContextForClient(clientId: number) {
        this.isCrmContextLoading = true;
        this.contractsService.getCrmContextForClient(clientId).subscribe({
            next: context => {
                this.crmContext = context;
                this.createEditContract.kamCustomerId = this.createEditContract.kamCustomerId ?? context.kamCustomerId ?? context.client?.kamCustomerId;
                if (!this.createEditContract.crmAccountId && context.accounts?.length === 1) {
                    this.createEditContract.crmAccountId = context.accounts[0].id;
                }
                this.isCrmContextLoading = false;
            },
            error: () => {
                this.isCrmContextLoading = false;
            }
        });
    }

    private loadCrmContextForContract(contractId: number) {
        this.isCrmContextLoading = true;
        this.contractsService.getCrmContextForContract(contractId).subscribe({
            next: context => {
                this.crmContext = context;
                this.isCrmContextLoading = false;
            },
            error: () => {
                this.isCrmContextLoading = false;
            }
        });
    }

    private syncContractFromForm() {
        const formValue = this.createEditContractForm.value;
        this.createEditContract.firstParty = formValue.firstPartyDropdown;
        this.createEditContract.firstPartyRep = formValue.firstPartyRepDropdown;
        this.createEditContract.secondParty = formValue.secondPartyDropdown;
        this.createEditContract.secondPartyRep = formValue.secondPartyRepDropdown;
    }

    private getErrorDetail(error: any, fallback: string): string {
        if (typeof error?.error === 'string' && error.error.trim()) {
            return error.error;
        }
        if (typeof error?.error?.message === 'string' && error.error.message.trim()) {
            return error.error.message;
        }
        if (typeof error?.message === 'string' && error.message.trim()) {
            return error.message;
        }
        return fallback;
    }
}
